import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faChevronUp, faChevronDown, faSpinner, faPlay, faChartLine, faClock, faUsers } from '@/fontawesome';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { APIRoutes } from '@/services/api.routes';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import { useUser } from '@/contexts/UserContext';
import { formatDate, formatCurrency } from '@/utils/helpers';
import { calculateSessionProfit, formatProfitPct, getProfitColorClass } from '@/utils/daytrade-session-profit';
import { getSubscriptionStatus } from '@/utils/subscription-status';
import { DAYTRADING_PRICING_PACKAGES } from '@/consts/pricing';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import PricingCard from '@/components/checkout/PricingCard';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import type { DayTradingSession } from '@/models/day-trading-session';
import type { SubscriptionPriceProduct, SubscriptionType, Subscription, EuroCent } from '@/models';
import Tooltip from '@/components/ui/Tooltip';
import NumberInput from '@/components/forms/NumberInput';

const PAGE_SIZE = 10;
const MIN_CAPITAL = 25_000; // Pattern-Day-Trading Mindestkapital (SEC Rule)
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
const MAX_FUTURE_YEARS = 10;

interface ChartDataPoint {
  date: string;
  dateLabel: string;
  dateMs: number;
  capital: number | null;
  capitalFuture: number | null;
  profit: number;
  isFuture: boolean;
}

export default function DayTradingLandingPage() {
  const { user } = useUser();
  const { products, loading: productsLoading } = useSubscriptionProducts();

  // Past sessions state
  const [pastSessions, setPastSessions] = useState<DayTradingSession[]>([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState(false);

  // Chart state
  const [chartSessions, setChartSessions] = useState<DayTradingSession[]>([]);
  const [chartLoading, setChartLoading] = useState(true);

  // Pricing state
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const pricingLoading = productsLoading || subscriptionsLoading;

  // Capital calculator state
  const [capital, setCapital] = useState<number>(50_000);
  const [futureYears, setFutureYears] = useState<number>(0);
  const [sinceDate, setSinceDate] = useState<string>(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split('T')[0];
  });
  const [earliestDate, setEarliestDate] = useState<string | undefined>(undefined);
  const capitalIsValid = capital >= MIN_CAPITAL;

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;
      const response = await APIRoutes.listDayTradingSessions({
        open: false,
        limit: PAGE_SIZE,
        next: cursorToken,
        sort: 'start',
        sortDirection: 'DESC',
      });
      setPastSessions(response.sessions);
      setSessionsTotal(response.total);
    } catch {
      setSessionsError(true);
    } finally {
      setSessionsLoading(false);
    }
  }, [currentPage]);

  const fetchChartSessions = useCallback(async () => {
    setChartLoading(true);
    try {
      const response = await APIRoutes.listDayTradingSessions({
        open: false,
        limit: 500,
        sort: 'start',
        sortDirection: 'ASC',
      });
      setChartSessions(response.sessions);
    } catch {
      // Chart data fetch failed - chart won't render
    } finally {
      setChartLoading(false);
    }
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      if (user !== null) {
        const subscriptionsResponse = await APIRoutes.getUserSubscriptions(user.id);
        setSubscriptions(subscriptionsResponse.subscriptions);
      }
    } catch {
      // Subscription fetch failed - cards render without the "already booked" state
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    fetchChartSessions();
  }, [fetchChartSessions]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Fetch earliest historical session — used as min for the "Berechnung ab" date input
  useEffect(() => {
    const fetchEarliest = async () => {
      try {
        const response = await APIRoutes.listDayTradingSessions({
          open: false,
          limit: 1,
          sort: 'start',
          sortDirection: 'ASC',
        });
        if (response.sessions.length > 0) {
          setEarliestDate(response.sessions[0].start.split('T')[0]);
        }
      } catch {
        // Ignore — input will simply have no min constraint
      }
    };
    fetchEarliest();
  }, []);

  // Clamp sinceDate up if it's earlier than the first available session
  useEffect(() => {
    if (earliestDate !== undefined && sinceDate < earliestDate) {
      setSinceDate(earliestDate);
    }
  }, [earliestDate, sinceDate]);

  // Debounced tracking for capital calculator changes (skip initial mount)
  const calculatorInitialRender = useRef(true);
  useEffect(() => {
    if (calculatorInitialRender.current) {
      calculatorInitialRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      analytics.track(AnalyticsEvents.DAYTRADING_CALCULATOR_CHANGED, { capital, sinceDate });
    }, 500);
    return () => clearTimeout(timer);
  }, [capital, sinceDate]);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (chartSessions.length === 0) return [];
    if (!capitalIsValid) return [];

    const startMs = new Date(sinceDate + 'T00:00:00').getTime();
    const visibleSessions = chartSessions.filter(s => new Date(s.start).getTime() >= startMs);

    let current = capital;
    const points: ChartDataPoint[] = [{
      date: new Date(startMs).toISOString(),
      dateLabel: new Date(startMs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      dateMs: startMs,
      capital,
      capitalFuture: null,
      profit: 0,
      isFuture: false,
    }];

    for (const session of visibleSessions) {
      const trades = session.state.trades ?? [];
      const { profitPct } = calculateSessionProfit(trades);
      const newCapital = current * (1 + profitPct / 100);
      const profit = newCapital - current;
      current = newCapital;
      const sessionMs = new Date(session.start).getTime();
      points.push({
        date: new Date(sessionMs).toISOString(),
        dateLabel: new Date(sessionMs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
        dateMs: sessionMs,
        capital: Math.round(current * 100) / 100,
        capitalFuture: null,
        profit: Math.round(profit * 100) / 100,
        isFuture: false,
      });
    }

    if (futureYears > 0 && points.length > 1 && capital > 0 && current > 0 && chartSessions.length >= 2) {
      // CAGR is computed from the ENTIRE historical session set (not filtered by sinceDate),
      // so the projected growth reflects the strategy's full-period annualized return.
      let fullMultiplier = 1;
      for (const session of chartSessions) {
        const { profitPct } = calculateSessionProfit(session.state.trades ?? []);
        fullMultiplier *= 1 + profitPct / 100;
      }
      const fullStartMs = new Date(chartSessions[0].start).getTime();
      const fullEndMs = new Date(chartSessions[chartSessions.length - 1].start).getTime();
      const fullYearsSpan = (fullEndMs - fullStartMs) / MS_PER_YEAR;
      const cagr = fullYearsSpan > 0 && fullMultiplier > 0
        ? Math.pow(fullMultiplier, 1 / fullYearsSpan) - 1
        : 0;

      const lastPoint = points[points.length - 1];
      const lastMs = new Date(lastPoint.date).getTime();
      // Bridge: last historical point also anchors the future area for a seamless connection
      lastPoint.capitalFuture = lastPoint.capital;

      let projected = current;
      for (let year = 1; year <= futureYears; year++) {
        const previousProjected = projected;
        projected = projected * (1 + cagr);
        const futureMs = lastMs + year * MS_PER_YEAR;
        points.push({
          date: new Date(futureMs).toISOString(),
          dateLabel: new Date(futureMs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
          dateMs: futureMs,
          capital: null,
          capitalFuture: Math.round(projected * 100) / 100,
          profit: Math.round((projected - previousProjected) * 100) / 100,
          isFuture: true,
        });
      }
    }

    return points;
  }, [chartSessions, capital, sinceDate, capitalIsValid, futureYears]);

  const lastHistoricalPoint = useMemo(() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (!chartData[i].isFuture && chartData[i].capital !== null) return chartData[i];
    }
    return null;
  }, [chartData]);

  const finalValue = lastHistoricalPoint?.capital ?? capital;
  const totalReturn = capital > 0 ? ((finalValue - capital) / capital) * 100 : 0;

  const lastFuturePoint = useMemo(() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (chartData[i].isFuture && chartData[i].capitalFuture !== null) return chartData[i];
    }
    return null;
  }, [chartData]);

  const projectedValue = lastFuturePoint?.capitalFuture ?? null;
  const projectedReturn = projectedValue !== null && capital > 0 ? ((projectedValue - capital) / capital) * 100 : 0;

  const chartMin = useMemo(() => {
    if (chartData.length === 0) return 0;
    const values = chartData.flatMap(d => {
      const result: number[] = [];
      if (d.capital !== null) result.push(d.capital);
      if (d.capitalFuture !== null) result.push(d.capitalFuture);
      return result;
    });
    if (values.length === 0) return 0;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1;
    return Math.floor((min - padding) / 1000) * 1000;
  }, [chartData]);

  const ChartTooltipContent = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartDataPoint }> }) => {
    if (active && payload !== undefined && payload.length > 0) {
      const data = payload[0].payload;
      const value = data.capital ?? data.capitalFuture;
      if (value === null) return null;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {data.dateLabel}
            {data.isFuture && <span className="ml-1 text-violet-500 dark:text-violet-400">(Prognose)</span>}
          </p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            ${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          {data.profit !== 0 && (
            <p className={`text-xs font-medium ${data.profit > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {data.profit > 0 ? '+' : ''}${data.profit.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const getProductByType = (type: SubscriptionType): SubscriptionPriceProduct | undefined => {
    return products.find(p => p.type === type && p.active);
  };

  const getMonthlyBasePrice = (productType: string): EuroCent | undefined => {
    const monthlyProduct = products.find(p => p.productType === productType && p.intervalMonths === 1);
    return monthlyProduct?.price;
  };

  // Check if any daytrading products are available from the backend
  const hasDaytradingProducts = DAYTRADING_PRICING_PACKAGES.some(
    config => getProductByType(config.type) !== undefined
  );

  return (
    <div className="min-h-screen pb-12">
      {/* Hero Section */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
            <FontAwesomeIcon icon={faBolt} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Daytrading
          </h1>
        </div>

        <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
          Nimm börsentäglich live an den Daytrading Sessions mit Sven teil!<br />
          Wir starten immer um <span className="font-semibold text-blue-600 dark:text-blue-400">15:15</span> und enden um <span className="font-semibold text-blue-600 dark:text-blue-400">16:15</span> - begleitet mit Live Stream und nachvollziehbaren Tradingideen
        </p>

        {/* Feature highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FontAwesomeIcon icon={faPlay} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Live-Sessions</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Echtzeit-Stream mit Kommentar</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Echtzeit-Tradingideen</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Ein- und Ausstiege live verfolgen</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Session-Historien</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Vergangene Sessions nachvollziehen</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Gemeinsam traden</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Simuliertes Portfolio verwalten</p>
            </div>
          </div>
        </div>
      </div>

      {/* Capital Progression Chart */}
      <div className="mb-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Kapitalentwicklung
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Wie hätte sich dein Kapital seit dem {new Date(sinceDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} entwickelt?
            </p>
          </div>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-start gap-x-6 gap-y-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3 h-10">
                <label className="text-sm text-gray-500 dark:text-gray-400 font-medium">Startkapital:</label>
                <NumberInput
                  value={String(capital)}
                  onChange={(digits) => setCapital(digits === '' ? 0 : Number(digits))}
                  suffix="$"
                  ariaLabel="Startkapital"
                  size="sm"
                  className={`inline-flex items-stretch w-36 rounded-lg border overflow-hidden bg-white dark:bg-gray-700 focus-within:ring-2 ${
                    capitalIsValid
                      ? 'border-gray-300 dark:border-gray-600 focus-within:ring-blue-500 focus-within:border-transparent'
                      : 'border-red-400 dark:border-red-500 focus-within:ring-red-500'
                  }`}
                />
              </div>
              {!capitalIsValid && (
                <p className="text-xs text-red-600 dark:text-red-400 ml-25">
                  Mindestkapital: ${MIN_CAPITAL.toLocaleString('de-DE')} (Pattern-Day-Trading-Regel)
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 h-10">
              <label className="text-sm text-gray-500 dark:text-gray-400 font-medium">Berechnung ab:</label>
              <input
                type="date"
                value={sinceDate}
                min={earliestDate}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === '') return;
                  if (earliestDate !== undefined && next < earliestDate) {
                    setSinceDate(earliestDate);
                    return;
                  }
                  setSinceDate(next);
                }}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <div className="flex items-center gap-3 h-10">
              <label htmlFor="future-years" className="text-sm text-gray-500 dark:text-gray-400 font-medium">Prognose-Zeitraum:</label>
              <input
                id="future-years"
                type="range"
                min={0}
                max={MAX_FUTURE_YEARS}
                step={1}
                value={futureYears}
                onChange={e => setFutureYears(Number(e.target.value))}
                className="w-32 cursor-pointer accent-violet-500"
                aria-label="Jahre in die Zukunft"
              />
              <span className="text-sm font-semibold text-violet-600 dark:text-violet-400 w-16">
                {futureYears} {futureYears === 1 ? 'Jahr' : 'Jahre'}
              </span>
            </div>
          </div>
          {chartLoading ? (
            <div className="p-12 flex justify-center">
              <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : chartData.length > 1 ? (
            <>
              <div className="p-4 sm:p-6" style={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="capitalGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="capitalFutureGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis
                      dataKey="dateMs"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      scale="time"
                      tick={{ fontSize: 11, fill: 'currentColor' }}
                      className="text-gray-500 dark:text-gray-400"
                      tickMargin={8}
                      minTickGap={40}
                      tickFormatter={(ms: number) => new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'currentColor' }}
                      className="text-gray-500 dark:text-gray-400"
                      tickFormatter={(v: number) => `$${(v / 1000).toLocaleString('de-DE')}k`}
                      tickMargin={4}
                      domain={[chartMin, 'dataMax']}
                    />
                    <RechartsTooltip content={<ChartTooltipContent />} />
                    <ReferenceLine y={capital} stroke="#9ca3af" strokeDasharray="4 4" strokeWidth={1} />
                    <Area
                      type="monotone"
                      dataKey="capital"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fill="url(#capitalGradient)"
                      dot={false}
                      activeDot={{ r: 4, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                      connectNulls={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="capitalFuture"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      fill="url(#capitalFutureGradient)"
                      dot={false}
                      activeDot={{ r: 4, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Aus{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    ${capital.toLocaleString('de-DE')}
                  </span>
                  {' '}wären{' '}
                  <span className={`font-semibold ${finalValue >= capital ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    ${finalValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {' '}geworden{' '}
                  <span className={`text-xs font-medium ${finalValue >= capital ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    ({finalValue >= capital ? '+' : ''}{totalReturn.toFixed(1)}%)
                  </span>
                </p>
                {futureYears > 0 && projectedValue !== null && (
                  <p className="text-sm text-violet-600 dark:text-violet-400 mt-1">
                    Prognose in {futureYears} {futureYears === 1 ? 'Jahr' : 'Jahren'}:{' '}
                    <span className="font-semibold">
                      ${projectedValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {' '}
                    <span className="text-xs font-medium">
                      ({projectedValue >= capital ? '+' : ''}{projectedReturn.toFixed(1)}%)
                    </span>
                  </p>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                  Basierend auf historischen Ergebnissen. Keine Garantie für zukünftige Renditen.
                </p>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {!capitalIsValid
                ? 'Bitte gib ein gültiges Startkapital ein, um die Entwicklung zu berechnen.'
                : 'Keine Daten für den gewählten Zeitraum verfügbar.'}
            </div>
          )}
        </div>
      </div>

      {/* Past Sessions Preview */}
      <div className="mb-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Vergangene Sessions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Ein Einblick in bisherige Daytrading-Sessions und deren Ergebnisse.
            </p>
          </div>

          {sessionsLoading && pastSessions.length === 0 ? (
            <div className="p-6 flex justify-center">
              <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : sessionsError ? (
            <div className="p-6 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                Session-Historien sind für Abonnenten verfügbar.
              </p>
            </div>
          ) : pastSessions.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              Noch keine Sessions vorhanden.
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className={`hidden md:block overflow-x-auto transition-opacity ${sessionsLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Datum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"># Trades</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Theoretisches Ergebnis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pastSessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {formatDate(session.start)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-white">
                          {session.state.trades?.filter(t => ['GOAL_REACHED', 'STOPLOSS'].includes(t.state))?.length ?? '-'}
                          <span style={{ opacity: 0.3 }}> / {session.state.trades?.length ?? '-'}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {(() => {
                            const trades = session.state.trades ?? [];
                            if (trades.length === 0) {
                              return <span className="text-sm text-gray-400 dark:text-gray-500">-</span>;
                            }
                            const { profitPct, budget, invested, profit } = calculateSessionProfit(trades);
                            const isPositive = profitPct > 0;
                            const isNegative = profitPct < 0;
                            return (
                              <Tooltip text={`Basierend auf einem Depot mit $${formatCurrency(budget, 'USD')}:\n$${formatCurrency(profit, 'USD')} Profit bei $${formatCurrency(invested, 'USD')} investiertem Kapital.`}>
                                <span className={`inline-flex items-center gap-1 text-sm font-semibold ${getProfitColorClass(profitPct)}`}>
                                  {isPositive && (<><FontAwesomeIcon icon={faChevronUp} className="w-3 h-3" /> +{formatProfitPct(profitPct)}%</>)}
                                  {isNegative && (<><FontAwesomeIcon icon={faChevronDown} className="w-3 h-3" /> {formatProfitPct(profitPct)}%</>)}
                                </span>
                              </Tooltip>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className={`md:hidden divide-y divide-gray-200 dark:divide-gray-700 transition-opacity ${sessionsLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                {pastSessions.map((session) => (
                  <div key={session.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDate(session.start)}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        Beendet
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        {session.state.trades?.filter(t => ['GOAL_REACHED', 'STOPLOSS'].includes(t.state))?.length ?? 0} / {session.state.trades?.length ?? 0} Trades
                      </span>
                      {(() => {
                        const trades = session.state.trades ?? [];
                        if (trades.length === 0) return null;
                        const { profitPct, budget, invested, profit } = calculateSessionProfit(trades);
                        const isPositive = profitPct > 0;
                        const isNegative = profitPct < 0;
                        return (
                          <Tooltip text={`Basierend auf einem Depot mit $${formatCurrency(budget, 'USD')}:\n$${formatCurrency(profit, 'USD')} Profit bei $${formatCurrency(invested, 'USD')} investiertem Kapital.`}>
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${getProfitColorClass(profitPct)}`}>
                              {isPositive && <FontAwesomeIcon icon={faChevronUp} className="w-2.5 h-2.5" />}
                              {isNegative && <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5" />}
                              {formatProfitPct(profitPct)}%
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>

              <PageBasedPagination
                currentPage={currentPage}
                totalCount={sessionsTotal}
                pageSize={PAGE_SIZE}
                onPageChange={setCurrentPage}
                loading={sessionsLoading}
              />
            </>
          )}
        </div>
      </div>

      {/* Pricing Section */}
      <div>
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Daytrading freischalten
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Wähle dein Abo und nimm ab sofort an allen Live Daytrading Sessions teil.
          </p>
        </div>

        {pricingLoading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : hasDaytradingProducts ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto items-stretch">
              {DAYTRADING_PRICING_PACKAGES.map((config) => {
                const product = getProductByType(config.type);
                if (product === undefined) return null;

                return (
                  <PricingCard
                    key={config.type}
                    product={product}
                    billingPeriod={config.period}
                    name={config.name}
                    subtitle={config.subtitle}
                    shortDescription={config.shortDescription}
                    features={config.features}
                    idealFor={config.idealFor}
                    popular={config.popular}
                    subscriptionStatus={getSubscriptionStatus(product, subscriptions, getProductByType, user?.featureConfig ?? null)}
                    monthlyBasePrice={config.period !== '1m' ? getMonthlyBasePrice(product.productType ?? '') : undefined}
                    highlightLongTerm
                  />
                );
              })}
            </div>
          </>
        ) : (
          <div className="max-w-xl mx-auto text-center">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
              <p className="text-gray-700 dark:text-gray-300">
                Das Daytrading-Abo ist bald verfügbar. Besuche unsere{' '}
                <Link to="/pricing" className="text-blue-600 dark:text-blue-400 hover:underline">Preisseite</Link>
                {' '}für aktuelle Angebote.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
